import React, { useMemo } from 'react';
import { ColumnHeaderContext } from './ColumnHeaderContext';
import { useColumnHeaderLogic } from '../hooks/useColumnHeaderLogic';
import type { ColumnHeaderProviderProps } from '../types';

const EMPTY_ARRAY: any[] = [];

export const ColumnHeaderProvider: React.FC<ColumnHeaderProviderProps> = ({ children, column, sort, items, filter, search, customFilterSlot }) => {
  // Get internal state and handlers from logic hook
  const {
    anchorEl,
    isOpen,
    handleOpen,
    handleClose,
    selectedValues,
    handleSelectAll,
    handleClearAll,
    handleToggleValue,
    handleApplyFilter,
    handleApplySort,
    searchTerm,
    handleSearchChange,
    filteredItems, // Filtered by search (if consumer doesn't provide onSearchChange)
  } = useColumnHeaderLogic({
    filterItems: items?.items || EMPTY_ARRAY,
    initialSelectedValues: filter?.initialSelectedValues || EMPTY_ARRAY,
    onFilter: filter?.onFilter,
    onSort: sort?.onSort,
    onSearchChange: search?.onChange,
    columnId: column.id,
    autoSelect: filter?.autoSelect ?? false, // Enable auto-select if configured
  });

  // Build context value
  const contextValue = useMemo(
    () => ({
      column,
      sort: {
        currentDirection: column.getIsSorted(),
        ascText: sort?.ascText || 'Sort A to Z',
        descText: sort?.descText || 'Sort Z to A',
        onApply: handleApplySort,
      },
      items: {
        items: filteredItems, // Filtered by search term (if no custom handler)
        count: items?.count,
        isLoading: items?.isLoading || false,
        isLoadingMore: items?.isLoadingMore,
        hasMore: items?.hasMore,
        onLoadMore: items?.onLoadMore,
      },
      filter: {
        selectedValues,
        onSelectAll: handleSelectAll,
        onClearAll: handleClearAll,
        onToggleValue: handleToggleValue,
        onApply: handleApplyFilter,
      },
      search: {
        term: searchTerm,
        onChange: handleSearchChange,
      },
      popover: {
        anchorEl,
        isOpen,
        onOpen: handleOpen,
        onClose: handleClose,
      },
      customFilterSlot,
    }),
    [
      column,
      sort,
      items,
      handleApplySort,
      selectedValues,
      handleSelectAll,
      handleClearAll,
      handleToggleValue,
      handleApplyFilter,
      search,
      searchTerm,
      handleSearchChange,
      anchorEl,
      isOpen,
      handleOpen,
      handleClose,
      customFilterSlot,
    ]
  );

  return <ColumnHeaderContext.Provider value={contextValue}>{children}</ColumnHeaderContext.Provider>;
};
